import React, { useState, useMemo } from "react";
import { OverallActivityChart } from './OverallActivityChart';
import PointerClickIcon from "../../../../assets/images/icons/pointer-click.svg";
import TopCountryVisitChart from "./TopCountryVisitChart";
import { useOverallSmartLinkAnalytics } from "../../../../queries/artistManger/smartLink.query";
import { SkeletonBox, OverallAnalyticsCardSkeleton } from "../LinkSkeletons";
import { STREAM_UI } from "../constants/allPlatformIcon";

export const Dashboard = ({ queriesEnabled = true }) => {
    const [range, setRange] = useState("all-time");
    const { data, isLoading } = useOverallSmartLinkAnalytics(range, { enabled: queriesEnabled });
    const overallData = data?.result || {};
    const totals = overallData?.totals || {};
    const timeline = overallData?.timeline || [];
    const topSites = data?.topSongs || [];
    const streamData = overallData?.streams || []
    const pageVisits = totals?.clicks || 0;
    const clickToService = totals?.clicksToService || 0;
    const referralData = overallData?.referrals || [];
    const locationData = overallData?.locations || [];

    const ctr = useMemo(() => {
        if (!pageVisits) return "0.00";
        return ((clickToService / pageVisits) * 100).toFixed(2);
    }, [pageVisits, clickToService]);


    // 24-hour labels
    const hours = Array.from({ length: 25 }, (_, i) => (i + 0).toString());

    // Sample statuses for each hour
    const statusData = [
        "OFF", "OFF", "ON", "ON", "DR", "DR", "ON", "SB", "SB", "ON", "ON", "DR",
        "DR", "SB", "ON", "ON", "ON", "DR", "OFF", "OFF", "ON", "SB", "OFF", "OFF", "OFF",
    ];

    // Convert status text into numeric Y values
    const statusMapping = { ON: 0.5, DR: 1.5, SB: 2.5, OFF: 3.5 };
    const yValues = statusData.map((status) => statusMapping[status]);

    const options = {
        chart: {
            type: "line",
            height: 350,
            // offsetX: -15,
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        stroke: {
            curve: "stepline",
            width: 3,
        },
        xaxis: {
            categories: hours,
            position: "top", // ✅ Move X-axis to top
            title: {
                text: "Hours (1–24)",
                style: { color: "#00067e", fontWeight: 600 },
            },
            labels: {
                style: { colors: "#333", fontWeight: 500 },
            },
        },
        yaxis: {
            min: 0,
            max: 4,
            tickAmount: 4,
            title: {
                show: false,
                text: "Status",
                style: { color: "#00067e", fontWeight: 600 },
            },
            labels: {
                formatter: (val) => {
                    const labels = ["ON", "DR", "SB", "OFF"];
                    return labels[val] || "";
                },
                style: { colors: "#333", fontWeight: 500 },
                offsetY: -32,
            },
        },
        tooltip: {
            x: { show: true },
            y: {
                formatter: (val) => {
                    const labels = ["ON", "DR", "SB", "OFF"];
                    return labels[val] || "";
                },
            },
        },
        colors: ["#00067e"],
        grid: {
            borderColor: "#e0e0e0",
            row: {
                colors: ["#e6f0ff", "#e8ffe8", "#fff5e6", "#ffe6e6"], // ON, DR, SB, OFF
                opacity: 0.7,
            },
        },
    };

    const series = [
        {
            name: "Status",
            data: yValues,
        },
    ];

    const ranges = [
        { key: "weekly", label: "Last 7 days" },
        { key: "monthly", label: "Last 30 days" },
        { key: "yearly", label: "Last 12 months" },
        { key: "all-time", label: "All time" },
    ];

    return (
        <div className="bg-white py-2">
            {/* Heading & Chart Section */}
            <div className="chart-wrapper mb-4">
                <div id="tour-dashboard-activity" className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="heading-wrapper">
                        <h2 className="font-bold text-lg text-theme_clr1">Overall Activity</h2>
                        <p className="font-bold text-base text-theme_clr1 text-opacity-70">This is the performance across all your links.</p>
                    </div>

                    <div className="flex gap-2 text-sm font-semibold">
                        {ranges.map((r) => (
                            <button
                                key={r.key}
                                onClick={() => range !== r.key && setRange(r.key)}
                                className={`px-5 py-2 rounded-lg border transition-all duration-200
                                         ${range === r.key
                                        ? "bg-[#ff6633] text-white border-[#ff6633]"
                                        : "text-[#ff6633] border-transparent hover:border-[#ff6633]/40"
                                    }`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Chart Section */}
                <div id="tour-dashboard-chart" className="border-2 border-theme_clr1 rounded-2xl p-4">

                    {isLoading ? (
                        <SkeletonBox className="h-[240px] w-full" />
                    ) : !timeline || timeline.length === 0 ? (
                        <div className="h-[240px] flex flex-col items-center justify-center text-center">
                            <div className="text-3xl mb-2">📊</div>
                            <p className="text-theme_clr1 font-semibold">
                                No chart data available
                            </p>
                            <p className="text-theme_clr1/60 text-sm mt-1">
                                Data will appear here once activity is recorded.
                            </p>
                        </div>
                    ) : (
                        <OverallActivityChart timeline={timeline} range={range} />
                    )}

                </div>
            </div>

            {/* Stats Card Grid Section */}
            <div id="tour-dashboard-stats" className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {isLoading ? (
                    <>
                        <OverallAnalyticsCardSkeleton />
                        <OverallAnalyticsCardSkeleton />
                        <OverallAnalyticsCardSkeleton />
                    </>
                ) : (
                    <>
                        <div className="border-[5px] border-theme_clr1 rounded-lg p-4">
                            <div className="icon-cover mb-2">
                                <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                                    <i className="bi bi-eye text-4xl text-theme_clr2"></i>
                                </span>
                            </div>
                            <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Page Visits</div>
                            <div className="text-xl font-bold text-theme_clr1">{pageVisits}</div>
                        </div>

                        <div className="border-[5px] border-theme_clr1 rounded-lg p-4">
                            <div className="icon-cover mb-2">
                                <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                                    <img src={PointerClickIcon} alt="Pointer Click Icon" />
                                </span>
                            </div>
                            <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Streaming Link Clicks</div>
                            <div className="text-xl font-bold text-theme_clr1">{clickToService}</div>
                        </div>

                        <div className="border-[5px] border-theme_clr1 rounded-lg p-4">
                            <div className="icon-cover mb-2">
                                <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                                    <img src={PointerClickIcon} alt="Pointer Click Icon" />
                                </span>
                            </div>
                            <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Clickthrough Rate</div>
                            <div className="text-xl font-bold text-theme_clr1">{ctr}%</div>
                        </div>
                    </>
                )}
            </div>

            {/* Grid Card Section */}
            <div id="tour-dashboard-top-grid" className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
                <div className="border-2 border-theme_clr1 rounded-2xl text-theme_clr1 p-4">
                    <h2 className="text-lg font-bold mb-4">Top Sites</h2>
                    {isLoading ? (
                        <SkeletonBox className="h-24 w-full" />
                    ) : topSites.length === 0 ? (
                        <EmptySectionState message="No top sites data available" />
                    ) : (
                        <ul className="space-y-2">
                            {topSites.map((site, index) => (
                                <li key={site.smartLinkId} className="text-base font-bold flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-x-2 gap-y-1">
                                        <span className="w-5">{index + 1}</span>
                                        {site.image ? (
                                            <img src={site.image} alt={site.title} className="w-12 h-12 rounded-md object-cover" />
                                        ) : (
                                            <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center text-gray-400">?</div>
                                        )}
                                        <span className="title">{site.title}</span>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-baseline justify-end gap-1">
                                            <span className="text-base font-bold text-theme_clr1">
                                                {site.clicks}
                                            </span>
                                            <span className="text-[11px] text-theme_clr1/60 font-medium">
                                                clicks
                                            </span>
                                        </div>
                                    </div>

                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="border-2 border-theme_clr1 rounded-2xl text-theme_clr1 p-4">
                    <h2 className="text-lg font-bold mb-4">Top Streaming Services</h2>
                    {isLoading ? (
                        <SkeletonBox className="h-24 w-full" />
                    ) : streamData.length === 0 ? (
                        <EmptySectionState message="No streaming service data available" />
                    ) : (
                        <ul className="space-y-2">
                            {streamData.map((stream, index) => {
                                const ui = STREAM_UI[stream.name] || {
                                    label: stream.name,
                                    icon: null,
                                };
                                return (
                                    <li
                                        key={stream.name}
                                        className="flex items-center justify-between gap-2 font-bold"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="w-5">{index + 1}</span>
                                            {ui.icon && (
                                                <img
                                                    src={ui.icon}
                                                    alt={ui.label}
                                                    className="w-5 h-5 object-contain"
                                                />
                                            )}
                                            <span>{ui.label}</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-theme_clr1">
                                                {stream.clicksToService}
                                                <span className="ml-1 text-xs text-theme_clr1/60">
                                                    ({stream.percentage}%)
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            <div id="tour-dashboard-sources" className="border-2 border-theme_clr1 rounded-2xl text-theme_clr1 p-4 mb-4">
                <div className="top-sources mb-4">
                    <div className="text-lg font-bold mb-3">Top Sources</div>
                    {isLoading ? (
                        <SkeletonBox className="h-32 w-full" />
                    ) : referralData.length === 0 ? (
                        <EmptySectionState message="No referral data available" />
                    ) : (
                        <div className="space-y-2 text-base font-bold">
                            {referralData.map((ref, index) => {
                                const isUrl = ref.name?.includes(".");
                                const link = isUrl ? `https://${ref.name}` : null;

                                return (
                                    <div
                                        key={ref.name}
                                        className="flex justify-between items-center"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="w-4 text-sm">{index + 1}</span>
                                            {link ? (
                                                <a
                                                    href={link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="hover:text-theme_clr2 break-all"
                                                >
                                                    {ref.name}
                                                </a>
                                            ) : (
                                                <span className="capitalize">{ref.name}</span>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <span className="font-bold">{ref.clicks}</span>
                                            <span className="ml-1 text-xs font-medium text-theme_clr1/60">
                                                clicks
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

            </div>

            <div id="tour-dashboard-countries" className="border-2 border-theme_clr1 rounded-2xl text-theme_clr1 p-4">
                <div className="info-wrapper">
                    <div className="heading-wrapper mb-3">
                        <h2 className="text-lg font-bold text-theme_clr1">
                            Top Countries by Visits
                        </h2>
                        <div className="text-base font-bold text-theme_clr1/70">
                            The countries your fans are clicking from.
                        </div>
                    </div>
                    {isLoading ? (
                        <SkeletonBox className="h-32 w-full" />
                    ) : !locationData || locationData.length === 0 ? (
                        <EmptySectionState message="No country visit data available" />
                    ) : (
                        <div className="flex flex-wrap justify-between items-end gap-4">
                            <TopCountryVisitChart
                                location={locationData}
                                totalClicks={pageVisits}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const EmptySectionState = ({ message }) => (
    <div className="h-[160px] flex flex-col items-center justify-center text-center">
        <div className="text-2xl mb-2">📭</div>
        <p className="text-theme_clr1 font-semibold">{message}</p>
        <p className="text-theme_clr1/60 text-sm mt-1">
            Data will appear here once activity is recorded.
        </p>
    </div>
);